// TestResults.jsx — "Signal" design system results screen, data-driven
//
// ── ROOT CAUSE OF THE MISSING SCORE (read before touching the calc logic) ──
// The previous version computed the overall percentage as:
//     totalScore / testTotalScore
// and required a parallel `sectionTotalScores` array to show any section
// score. Neither field exists in the real submit-test response —
// `testTotalScore` is always `null`, and sections never carried a "total
// points" figure at all. The API instead expresses "how good is this
// score" as a *range*: `minInterpretationValue`/`maxInterpretationValue`
// for the overall score, `minSecInterValue`/`maxSecInterValue` per section.
// So the fix is two-fold:
//   1. Use `totalScore` / `sectionScore` as the actual score values
//      (never `testTotalScore`, which is a legacy/unused field here).
//   2. Compute percentage as the score's *position* inside its min/max
//      range (see calculateScorePercentage in resultScoreUtils.js), not as
//      score-over-total.
// This component now consumes the raw `data` object from the submit-test
// response directly (see extractResultData) instead of a hand-mapped
// intermediate shape, so there's no lossy translation step in between.
//
// UPDATE: the history endpoint now sends a real `totalTestScore` (and each
// section a `totalSectionScore`), so score/total is used as the primary
// percentage source when present, falling back to the min/max range calc
// above only when it isn't. See resultScoreUtils.js for the details.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  CheckCircle,
  Target,
  Brain,
  Heart,
  Users,
  Lightbulb,
  Star,
  BarChart3,
  Compass,
  ShieldOff,
} from "lucide-react";
import "./TestResults.scss";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import {
  toFiniteNumber,
  calculateScorePercentage,
  calculateScoreFractionPercentage,
  formatScoreFraction,
  formatRange,
  normalizeSections,
  resolveVisibility,
  extractResultData,
  buildSectionView,
} from "./resultScoreUtils";

const DIMENSION_ICONS = [
  Brain,
  Heart,
  Users,
  Lightbulb,
  Star,
  BarChart3,
  Target,
  Compass,
];
const getDimensionIcon = (index) =>
  DIMENSION_ICONS[index % DIMENSION_ICONS.length];

// ─── Score ring — percentage as an animated radial progress ───────────────
const ScoreRing = ({ pct, mounted, size = 130, stroke = 10 }) => {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const safePct = pct === null ? 0 : Math.max(0, Math.min(100, pct));
  const offset = circumference * (1 - safePct / 100);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="score-ring-svg"
      role="img"
      aria-label={pct === null ? "Score unavailable" : `Score ${pct}%`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        className="ring-track"
        style={{ strokeWidth: stroke }}
      />
      {pct !== null && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className={`ring-progress ${mounted ? "is-in" : ""}`}
          style={{
            "--circumference": circumference,
            "--offset": offset,
            strokeWidth: stroke,
          }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
    </svg>
  );
};

// ─── Range meter — shows exactly where a score sits inside its min/max ────
const RangeMeter = ({ score, min, max, mounted, label }) => {
  const pct = calculateScorePercentage(score, min, max);
  const minNum = toFiniteNumber(min);
  const maxNum = toFiniteNumber(max);
  if (pct === null || minNum === null || maxNum === null) return null;

  return (
    <div className="range-meter">
      {label && <span className="range-meter-label">{label}</span>}
      <div className="range-meter-track">
        <span
          className={`range-meter-fill ${mounted ? "is-in" : ""}`}
          style={{ "--pct": `${pct}%` }}
        />
        <span
          className={`range-meter-marker ${mounted ? "is-in" : ""}`}
          style={{ "--pct": `${pct}%` }}
        />
      </div>
      <div className="range-meter-scale">
        <span>{Math.min(minNum, maxNum)}</span>
        <span>{Math.max(minNum, maxNum)}</span>
      </div>
    </div>
  );
};

// ─── Radar chart — every section plotted on one profile shape ─────────────
const RadarChart = ({ labels, values, mounted }) => {
  const size = 300;
  const center = size / 2;
  const maxR = 96;
  const levels = 4;
  const count = labels.length;

  if (count < 3) return null;

  const angleStep = (Math.PI * 2) / count;
  const pointFor = (r, i) => {
    const angle = -Math.PI / 2 + angleStep * i;
    return [center + r * Math.cos(angle), center + r * Math.sin(angle)];
  };

  const gridPolygons = Array.from({ length: levels }, (_, lvl) => {
    const r = maxR * ((lvl + 1) / levels);
    return Array.from({ length: count }, (_, i) =>
      pointFor(r, i).join(","),
    ).join(" ");
  });

  const axisLines = Array.from({ length: count }, (_, i) => pointFor(maxR, i));
  const dataPoints = values.map((v, i) => pointFor(((v ?? 0) / 100) * maxR, i));
  const dataPolygon = dataPoints.map((p) => p.join(",")).join(" ");
  const labelPoints = Array.from({ length: count }, (_, i) =>
    pointFor(maxR + 32, i),
  );

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="radar-svg"
      role="img"
      aria-label="Section score radar chart"
    >
      {gridPolygons.map((pts, idx) => (
        <polygon key={idx} points={pts} className="radar-grid" />
      ))}
      {axisLines.map(([x, y], i) => (
        <line
          key={i}
          x1={center}
          y1={center}
          x2={x}
          y2={y}
          className="radar-axis"
        />
      ))}
      <polygon
        points={dataPolygon}
        className={`radar-data ${mounted ? "is-in" : ""}`}
      />
      {dataPoints.map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r="4"
          className={`radar-dot ${mounted ? "is-in" : ""}`}
          style={{ "--delay": `${500 + i * 45}ms` }}
        />
      ))}
      {labelPoints.map(([x, y], i) => {
        const anchor =
          x > center + 4 ? "start" : x < center - 4 ? "end" : "middle";
        const label =
          labels[i].length > 16 ? `${labels[i].slice(0, 14)}…` : labels[i];
        return (
          <text key={i} x={x} y={y} textAnchor={anchor} className="radar-label">
            <tspan x={x} dy="-4">
              {label}
            </tspan>
            <tspan x={x} dy="14" className="radar-label-pct">
              {values[i] === null ? "—" : `${values[i]}%`}
            </tspan>
          </text>
        );
      })}
    </svg>
  );
};

const TestResults = ({
  results,
  hasAlreadyTaken,
  onRetakeTest,
  onBackToDashboard,
  // Result-visibility flags. Each accepts an explicit prop; if omitted, the
  // same-named flag on the API payload is used instead; if that's also
  // absent, the section is shown by default. See resolveVisibility().
  showResultsToUser,
  showScore,
  showRecommendation,
  showInterpretation,
  showSectionRecommendation,
  showSectionInterpretation,
}) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const cardRefs = useRef([]);

  // ── Normalize the API payload once ──────────────────────────────────────
  const data = useMemo(() => extractResultData(results), [results]);

  const canShowResults = resolveVisibility(
    showResultsToUser,
    data?.showResultsToUser,
    true,
  );
  const scoreVisible = resolveVisibility(showScore, data?.showScore, true);
  const interpretationVisible = resolveVisibility(
    showInterpretation,
    data?.showInterpretation,
    true,
  );
  const recommendationVisible = resolveVisibility(
    showRecommendation,
    data?.showRecommendation,
    true,
  );
  const sectionInterpretationVisible = resolveVisibility(
    showSectionInterpretation,
    data?.showSectionInterpretation,
    true,
  );
  const sectionRecommendationVisible = resolveVisibility(
    showSectionRecommendation,
    data?.showSectionRecommendation,
    true,
  );

  const totalScore = toFiniteNumber(data?.totalScore);
  // `totalTestScore` is the real "out of" total, now sent by the history
  // endpoint. It's the most trustworthy source for a percentage — unlike
  // the interpretation range, it's present even when no interpretation
  // matched this score. `testTotalScore` is accepted too in case an older
  // caller still uses that name. Both are guarded against 0 (seen on a
  // couple of legacy/duplicated tests) inside calculateScoreFractionPercentage.
  const testTotalScore = toFiniteNumber(
    data?.totalTestScore ?? data?.testTotalScore,
  );
  const minInterpretationValue = toFiniteNumber(data?.minInterpretationValue);
  const maxInterpretationValue = toFiniteNumber(data?.maxInterpretationValue);
  const minRecommendationValue = toFiniteNumber(data?.minRecommendationValue);
  const maxRecommendationValue = toFiniteNumber(data?.maxRecommendationValue);

  const overallFractionPct = scoreVisible
    ? calculateScoreFractionPercentage(totalScore, testTotalScore)
    : null;
  const overallRangePct = scoreVisible
    ? calculateScorePercentage(
        totalScore,
        minInterpretationValue,
        maxInterpretationValue,
      )
    : null;
  const overallPct =
    overallFractionPct !== null ? overallFractionPct : overallRangePct;
  const overallFraction = scoreVisible
    ? formatScoreFraction(totalScore, testTotalScore ?? maxInterpretationValue)
    : null;
  const overallRange = formatRange(
    minInterpretationValue,
    maxInterpretationValue,
  );

  const sections = useMemo(
    () => normalizeSections(data?.sections),
    [data?.sections],
  );
  const sectionViews = useMemo(
    () => sections.map((s, i) => buildSectionView(s, i)),
    [sections],
  );
  const sectionCount = sectionViews.length;

  // Only used for "top strength" / "growth focus" callouts — requires at
  // least one section with a real percentage.
  const scoredSections = useMemo(
    () => sectionViews.filter((s) => typeof s.pct === "number"),
    [sectionViews],
  );
  const topSection = scoredSections.length
    ? scoredSections.reduce((a, b) => (b.pct > a.pct ? b : a))
    : null;
  const growthSection = scoredSections.length
    ? scoredSections.reduce((a, b) => (b.pct < a.pct ? b : a))
    : null;
  const hasDistinctGrowth =
    growthSection && topSection && growthSection.id !== topSection.id;

  const radarValues = useMemo(
    () => sectionViews.map((s) => s.pct),
    [sectionViews],
  );

  const handleRetakeTest = () => {
    if (onRetakeTest) onRetakeTest();
  };

  const handleBackToDashboard = () => {
    if (onBackToDashboard) onBackToDashboard();
    else navigate("/dashboard");
  };

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Scroll-spy: keep the section rail's active node in sync with what's
  // actually in view, so the rail reads as a live position indicator.
  useEffect(() => {
    if (!sectionCount) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.sectionIndex);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );

    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sectionCount]);

  const jumpToSection = (index) => {
    const el = cardRefs.current[index];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("flash");
    void el.offsetWidth;
    el.classList.add("flash");
  };

  const railLabel = useMemo(
    () => (sectionCount ? `${activeIndex + 1} / ${sectionCount}` : ""),
    [activeIndex, sectionCount],
  );

  const showScoreOverview =
    scoreVisible &&
    (overallPct !== null || totalScore !== null || sectionCount > 0);
  const showInterpretationCard =
    interpretationVisible &&
    (data?.interpretationTitle || data?.interpretationDescription);
  const showRecommendationCard =
    recommendationVisible &&
    (data?.recommendationTitle || data?.recommendationDescription);

  return (
    <div className={`test-results ${mounted ? "is-mounted" : ""}`}>
      {/* ── Sticky header bar ── */}
      <div className="results-header">
        <div className="header-left">
          <div className="personality-badge">
            <Trophy className="badge-icon" />
            Assessment Complete
          </div>
          {hasAlreadyTaken && (
            <div className="retaken-notice">
              <CheckCircle className="notice-icon" />
              Previously Completed
            </div>
          )}
        </div>

        <div className="header-center">
          <h1>Your Self-Discovery Results</h1>
          <p>Discover the unique aspects that make you who you are</p>
        </div>

        <div className="header-right">
          {overallPct !== null ? (
            <span className="dimension-counter">{overallPct}% overall</span>
          ) : totalScore !== null ? (
            <span className="dimension-counter">{totalScore} pts</span>
          ) : (
            sectionCount > 0 && (
              <span className="dimension-counter">{railLabel}</span>
            )
          )}
        </div>
      </div>

      {/* ── Section rail — jump nav + live readout ── */}
      {canShowResults && sectionCount > 0 && (
        <div
          className="dimension-rail"
          role="tablist"
          aria-label="Jump to section"
        >
          <div className="dimension-rail-track">
            {sectionViews.map((section, index) => {
              const IconComponent = getDimensionIcon(index);
              const isActive = index === activeIndex;
              return (
                <button
                  key={section.id ?? index}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`Jump to ${section.title}`}
                  className={`rail-node ${isActive ? "active" : ""}`}
                  onClick={() => jumpToSection(index)}
                >
                  <IconComponent className="rail-node-icon" />
                  <span className="rail-node-label">{section.title}</span>
                  {scoreVisible && typeof section.pct === "number" && (
                    <span className="rail-node-pct">{section.pct}%</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Scrollable body ── */}
      <div className="results-container">
        {/* Celebration hero */}
        <div className="results-hero">
          <div className="hero-trophy">
            <Trophy className="hero-trophy-icon" />
            <span className="burst" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
              <i />
              <i />
            </span>
          </div>
          <h2>Your results are ready</h2>
          <p>Here&rsquo;s what your answers reveal, section by section.</p>
        </div>

        {!canShowResults ? (
          // ── Results intentionally hidden from the user ──
          <div
            className="results-hidden-state reveal"
            style={{ "--delay": "0ms" }}
          >
            <span className="results-hidden-icon">
              <ShieldOff />
            </span>
            <h3>Your results have been recorded</h3>
            <p>
              Detailed scoring for this assessment isn&rsquo;t shown here. Reach
              out to your administrator if you&rsquo;d like to discuss your
              results.
            </p>
          </div>
        ) : (
          <>
            {/* Overall score + key insights */}
            {showScoreOverview && (
              <div
                className="score-overview reveal"
                style={{ "--delay": "0ms" }}
              >
                {overallPct !== null ? (
                  <div className="score-ring-card">
                    <ScoreRing pct={overallPct} mounted={mounted} />
                    <div className="score-ring-readout">
                      <span className="score-ring-value">{overallPct}%</span>
                      <span className="score-ring-label">Overall Score</span>
                    </div>
                    {overallFraction && (
                      <span className="score-ring-fraction">
                        {overallFraction} points
                      </span>
                    )}
                    {overallRange && (
                      <span className="score-ring-range">
                        Range {overallRange}
                      </span>
                    )}
                  </div>
                ) : (
                  totalScore !== null && (
                    // No interpretation range came back for this attempt, so
                    // there's nothing to compute a percentage against — show
                    // the raw score instead of hiding it entirely.
                    <div className="score-ring-card score-ring-card--plain">
                      <span className="score-plain-value">{totalScore}</span>
                      <span className="score-ring-label">Total Score</span>
                    </div>
                  )
                )}

                <div className="insight-card">
                  <h3>Key Insights</h3>
                  <ul>
                    {topSection && (
                      <li className="insight-row">
                        <span className="insight-icon top">
                          <Star />
                        </span>
                        <div className="insight-copy">
                          <span className="insight-label">Top Strength</span>
                          <span className="insight-value">
                            {topSection.title} · {topSection.pct}%
                          </span>
                        </div>
                      </li>
                    )}
                    {hasDistinctGrowth && (
                      <li className="insight-row">
                        <span className="insight-icon growth">
                          <Target />
                        </span>
                        <div className="insight-copy">
                          <span className="insight-label">Growth Focus</span>
                          <span className="insight-value">
                            {growthSection.title} · {growthSection.pct}%
                          </span>
                        </div>
                      </li>
                    )}
                    <li className="insight-row">
                      <span className="insight-icon">
                        <BarChart3 />
                      </span>
                      <div className="insight-copy">
                        <span className="insight-label">Sections Analyzed</span>
                        <span className="insight-value">{sectionCount}</span>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Radar profile across every section */}
            {scoreVisible && sectionCount >= 3 && (
              <div
                className="radar-section reveal"
                style={{ "--delay": "40ms" }}
              >
                <div className="section-header">
                  <span className="section-icon">
                    <BarChart3 />
                  </span>
                  <div className="section-title-content">
                    <h3>Your Section Radar</h3>
                    <p>How each section compares, at a glance</p>
                  </div>
                </div>
                <div className="radar-wrap">
                  <RadarChart
                    labels={sectionViews.map((s) => s.title)}
                    values={radarValues}
                    mounted={mounted}
                  />
                </div>
              </div>
            )}

            {/* Interpretation + Recommendation cards */}
            {(showInterpretationCard || showRecommendationCard) && (
              <div className="results-summary">
                {showInterpretationCard && (
                  <div
                    className="personality-card reveal"
                    style={{ "--delay": "80ms" }}
                  >
                    <div className="cardheader">
                      <span className="card-icon">
                        <Brain />
                      </span>
                      <h3>{data?.interpretationTitle || "Your Overview"}</h3>
                    </div>
                    <div className="card-content">
                      {data?.interpretationDescription && (
                        <p>{data.interpretationDescription}</p>
                      )}
                      {scoreVisible && (
                        <RangeMeter
                          score={totalScore}
                          min={minInterpretationValue}
                          max={maxInterpretationValue}
                          mounted={mounted}
                          label="Where your score falls"
                        />
                      )}
                    </div>
                  </div>
                )}

                {showRecommendationCard && (
                  <div
                    className="personality-card reveal"
                    style={{ "--delay": "120ms" }}
                  >
                    <div className="cardheader">
                      <span className="card-icon">
                        <Lightbulb />
                      </span>
                      <h3>
                        {data?.recommendationTitle ||
                          "Recommendations for Growth"}
                      </h3>
                    </div>
                    <div className="card-content">
                      {data?.recommendationDescription && (
                        <p>{data.recommendationDescription}</p>
                      )}
                      {scoreVisible && (
                        <RangeMeter
                          score={totalScore}
                          min={minRecommendationValue}
                          max={maxRecommendationValue}
                          mounted={mounted}
                          label="Applicable range"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Section results */}
            {sectionCount > 0 && (
              <div className="section-results">
                <div className="section-header">
                  <span className="section-icon">
                    <BarChart3 />
                  </span>
                  <div className="section-title-content">
                    <h3>Detailed Section Analysis</h3>
                    <p>Explore different aspects of your results</p>
                  </div>
                </div>

                <div className="personality-dimensions">
                  {sectionViews.map((section, index) => {
                    const IconComponent = getDimensionIcon(index);
                    const isTop = topSection && section.id === topSection.id;
                    const isGrowth =
                      hasDistinctGrowth && section.id === growthSection.id;
                    return (
                      <div
                        key={section.id ?? index}
                        ref={(el) => (cardRefs.current[index] = el)}
                        data-section-index={index}
                        className="dimension-card reveal"
                        style={{ "--delay": `${160 + index * 50}ms` }}
                      >
                        {isTop && (
                          <span className="dimension-tag tag-top">
                            Top Strength
                          </span>
                        )}
                        {isGrowth && (
                          <span className="dimension-tag tag-growth">
                            Growth Focus
                          </span>
                        )}

                        <div className="dimension-header">
                          <span className="dimension-index">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <div className="dimension-icon">
                            <IconComponent className="dimension-icon-svg" />
                          </div>
                          <h4>{section.title}</h4>
                        </div>

                        {scoreVisible &&
                          (typeof section.pct === "number" ? (
                            <div className="dimension-score">
                              <div className="dimension-score-bar">
                                <span
                                  className={`dimension-score-fill ${mounted ? "is-in" : ""}`}
                                  style={{ "--pct": `${section.pct}%` }}
                                />
                              </div>
                              <span className="dimension-score-value">
                                {section.fraction ?? `${section.pct}%`}
                              </span>
                            </div>
                          ) : (
                            section.score !== null && (
                              // No section-level range came back for this
                              // attempt — show the raw score rather than a
                              // percentage bar we can't honestly compute.
                              <div className="dimension-score dimension-score--plain">
                                <span className="dimension-score-value">
                                  {section.score} pts
                                </span>
                              </div>
                            )
                          ))}

                        <div className="dimension-content">
                          {sectionInterpretationVisible &&
                            (section.interpretationTitle ||
                              section.interpretationDescription) && (
                              <div className="dimension-interpretation">
                                <h5>
                                  {section.interpretationTitle || "Analysis"}
                                </h5>
                                {section.interpretationDescription && (
                                  <p>{section.interpretationDescription}</p>
                                )}
                              </div>
                            )}

                          {sectionRecommendationVisible &&
                            (section.recommendationTitle ||
                              section.recommendationDescription) && (
                              <div className="dimension-recommendation">
                                <h5>
                                  {section.recommendationTitle ||
                                    "Recommendation"}
                                </h5>
                                {section.recommendationDescription && (
                                  <p>{section.recommendationDescription}</p>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Sticky footer ── */}
      <div className="results-actions">
        <ButtonWithIcon
          icon="material-symbols:arrow-back"
          text="Back to Dashboard"
          onClick={handleBackToDashboard}
          className="dashboard-button"
        />
        <ButtonWithIcon
          icon="material-symbols:rotate-left"
          text="Retake Assessment"
          onClick={handleRetakeTest}
          className="retake-button"
        />
      </div>
    </div>
  );
};

export default TestResults;
